import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { reportsApi, type ZaikoKinButton } from '../../api/reports.js';
import { ApiError } from '../../api/client.js';
import { ReportBody } from './ReportPage.js';

/**
 * 정의 기반 리포트 zaiko-stock(재고 확인) 위에, 원본 zaiko/zaiko_list.php:206-229
 * 의 즐겨찾기 바로가기 버튼 4개와 각 행의 즐겨찾기 등록 버튼(zaiko_load_more_data.php:
 * 121-136)을 얹은 화면. 표·필터·정렬·CSV 는 ReportBody 가 그대로 그린다 -
 * randombox-prize-ranking 과 같은 방식으로, 새 화면이라고 표 렌더링을 다시
 * 만들지 않는다(이 화면을 2026-09-04에 다시 디자인할 때도 ReportBody 는 건드리지
 * 않았다 - 같은 컴포넌트를 쓰는 다른 리포트 화면 50여 개가 영향을 받기 때문에,
 * 이 페이지 자체의 구조 - 즐겨찾기 막대·행별 등록 칩·이름 수정 모달 - 만 다시 짰다).
 *
 * 원본은 버튼을 누르면 그 즐겨찾기 항목만 걸러 보여주는 별도 페이지
 * (zaiko_list_kin.php)로 이동했다. 여기서는 페이지를 옮기지 않고, 같은 화면에
 * kinKubun 필터(report.registry.ts, hidden: true)를 걸어 같은 표를 다시
 * 그린다 - 검색어·카테고리 등 다른 조건과 함께 걸 수 있어 더 유연하다.
 *
 * 1~3번(마이디예 예약대기 · 이벤트상품 · 쿠팡)만 쓴다. 4번은 이름대로 "미사용"
 * 이라 눌러서 거르는 대상에 넣지 않았을 뿐 아니라(2026-09-04 요청 전까지는
 * 회색 비활성 버튼으로 보여줬다), 이제는 즐겨찾기 목록(상단 버튼·행마다의
 * 등록 버튼) 어디에도 아예 안 보이게 뺐다 - 어차피 못 쓰는 버튼을 굳이
 * 보여줄 이유가 없다는 요청. 이름 표시·수정(KinButtonEditModal)만은 그대로
 * 남긴다 - 나중에 4번을 실제로 쓰기로 하면 여기서 이름을 먼저 정해야 하니까.
 */
const CLICKABLE_KUBUN = new Set([1, 2, 3]);

export function ZaikoStockPage() {
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const activeKubun = params.get('kinKubun') ?? '';

  const buttons = useQuery({
    queryKey: ['report', 'zaiko-stock', 'kin-buttons'],
    queryFn: () => reportsApi.zaikoKinButtons(),
  });

  const clickableButtons = buttons.data?.filter((b) => CLICKABLE_KUBUN.has(b.kubun)) ?? [];
  // 행별 등록 칩의 title(툴팁)에 실제 이름을 보여주려고 kubun -> 이름을 미리 모아둔다.
  const namesByKubun = Object.fromEntries((buttons.data ?? []).map((b) => [b.kubun, b.name])) as Record<
    number,
    string
  >;

  function toggleKubun(kubun: number) {
    const next = new URLSearchParams(params);
    if (activeKubun === String(kubun)) {
      next.delete('kinKubun');
      next.delete('leftsFrom');
      next.delete('leftsTo');
    } else {
      next.set('kinKubun', String(kubun));
      // 재고 시작/끝은 즐겨찾기로 걸러 볼 때는 의미가 없다 - 재고와 상관없이
      // 등록해 둔 것 전부를 보여줘야 한다. report.registry.ts 의 leftsFrom/
      // leftsTo 가 defaultUnless: 'kinKubun' 을 갖고 있어서, kinKubun 이 와
      // 있으면 서버가 기본값(1..1)을 되살리지 않는다 - 그래서 여기서는 그냥
      // 지워서 칸을 비우기만 하면 된다 (0/99999 같은 임시값을 넣을 필요가 없다).
      next.delete('leftsFrom');
      next.delete('leftsTo');
    }
    next.delete('page');
    setParams(next, { replace: true });
  }

  return (
    <>
      <ReportBody
        reportId="zaiko-stock"
        headerExtra={
          <div className="zaiko-fav-bar">
            {buttons.isLoading ? (
              // 버튼 이름을 불러오는 동안 자리가 비었다 채워지며 레이아웃이
              // 튀는 대신, 크기가 비슷한 자리표시자를 먼저 보여준다.
              <div className="zaiko-fav-skeleton" aria-hidden="true">
                <div className="zaiko-fav-skeleton__pill" style={{ width: 96 }} />
                <div className="zaiko-fav-skeleton__pill" style={{ width: 78 }} />
                <div className="zaiko-fav-skeleton__pill" style={{ width: 64 }} />
              </div>
            ) : (
              <div className="tab-group" role="group" aria-label="즐겨찾기 필터">
                {clickableButtons.map((b) => {
                  const active = activeKubun === String(b.kubun);
                  return (
                    <button
                      key={b.seq}
                      type="button"
                      className={active ? 'tab-btn tab-btn--active' : 'tab-btn'}
                      aria-pressed={active}
                      onClick={() => toggleKubun(b.kubun)}
                      title={
                        active
                          ? '다시 누르면 필터가 풀립니다.'
                          : '눌러서 이 즐겨찾기에 등록된 상품만 봅니다.'
                      }
                    >
                      {b.name || `즐겨찾기${b.kubun}`}
                      {active && (
                        <span className="zaiko-fav-bar__clear" aria-hidden="true">
                          {' '}
                          ×
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditing(true)}>
              수정
            </button>
          </div>
        }
        extraColumn={{
          label: '즐겨찾기',
          render: (row) => (
            <FavoriteButtons
              goodsIdx={String(row.goods_idx ?? '')}
              barcode={String(row.barcode ?? '')}
              activeKubun={activeKubun === '' ? null : Number(activeKubun)}
              namesByKubun={namesByKubun}
            />
          ),
        }}
      />

      {editing && buttons.data && (
        <KinButtonEditModal buttons={buttons.data} onClose={() => setEditing(false)} />
      )}
    </>
  );
}

/**
 * 행마다 있는 즐겨찾기 버튼.
 *
 * 평소(activeKubun 없음)에는 원본 zaiko_load_more_data.php:121-136 처럼
 * 등록 버튼을 보여준다 - 누르면 confirm() 으로 물어보고 등록·중복 여부를
 * alert() 로 알린다. 숫자만으로는 무슨 즐겨찾기인지 짐작하기 어려워서, 위
 * 즐겨찾기 막대의 실제 이름을 title(마우스 오버 툴팁)로 붙여준다 - 화면에
 * 보이는 글자(숫자)는 그대로라 기존 동작·테스트에는 영향이 없다.
 * CLICKABLE_KUBUN 에 없는 번호(지금은 4번, "미사용")는 여기서도 안 보여준다 -
 * 등록해봐야 위 막대로 걸러 볼 수 없는 죽은 데이터만 쌓인다.
 *
 * 상단 버튼으로 특정 즐겨찾기만 걸러 보는 중(activeKubun 있음)에는 원본
 * zaiko_load_more_data_kin.php:82-93 처럼 등록 버튼들 대신 "해제" 버튼
 * 하나만 보여준다 - 지금 보고 있는 목록이 이미 그 즐겨찾기에 속한 상품들이라,
 * 다시 등록할 게 아니라 뺄 대상이기 때문이다. 해제하면 목록이 다시 조회되어
 * 그 행이 화면에서 사라진다.
 */
function FavoriteButtons({
  goodsIdx,
  barcode,
  activeKubun,
  namesByKubun,
}: {
  goodsIdx: string;
  barcode: string;
  activeKubun: number | null;
  namesByKubun: Record<number, string>;
}) {
  const queryClient = useQueryClient();

  const add = useMutation({
    mutationFn: (kubun: number) => reportsApi.addZaikoFavorite({ kubun, goodsIdx, barcode }),
    onSuccess: (result) => {
      window.alert(result.duplicate ? '이미 등록되었습니다.' : '즐겨찾기에 등록되었습니다.');
    },
    onError: (error) => {
      window.alert(error instanceof ApiError ? error.message : '등록에 실패했습니다.');
    },
  });

  const remove = useMutation({
    mutationFn: (kubun: number) => reportsApi.removeZaikoFavorite({ kubun, goodsIdx, barcode }),
    onSuccess: () => {
      // 지금 이 kubun 으로 걸러 보고 있으므로, 해제된 행이 목록에서 빠지도록
      // 다시 조회한다 (ReportBody 의 data 쿼리키가 ['report','zaiko-stock',...] 다).
      void queryClient.invalidateQueries({ queryKey: ['report', 'zaiko-stock'] });
    },
    onError: (error) => {
      window.alert(error instanceof ApiError ? error.message : '해제에 실패했습니다.');
    },
  });

  if (activeKubun !== null) {
    const name = namesByKubun[activeKubun] || `즐겨찾기${activeKubun}`;
    return (
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        disabled={!goodsIdx || !barcode || remove.isPending}
        title={`'${name}'에서 해제`}
        onClick={() => {
          if (window.confirm('즐겨찾기에서 해제하시겠습니까?')) remove.mutate(activeKubun);
        }}
      >
        해제
      </button>
    );
  }

  return (
    <div className="zaiko-fav-picker" role="group" aria-label="즐겨찾기 등록">
      {[...CLICKABLE_KUBUN].map((kubun) => {
        const name = namesByKubun[kubun] || `즐겨찾기${kubun}`;
        return (
          <button
            key={kubun}
            type="button"
            className="zaiko-fav-picker__btn"
            disabled={!goodsIdx || !barcode || add.isPending}
            title={`'${name}'에 추가`}
            onClick={() => {
              if (window.confirm(`즐겨찾기 ${kubun}에 추가하시겠습니까?`)) add.mutate(kubun);
            }}
          >
            {kubun}
          </button>
        );
      })}
    </div>
  );
}

/** 원본: zaiko/zaiko_kin_button.php 의 mode=add(폼) + mode=addexe(저장). */
function KinButtonEditModal({
  buttons,
  onClose,
}: {
  buttons: ZaikoKinButton[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [names, setNames] = useState<Record<number, string>>(() =>
    Object.fromEntries(buttons.map((b) => [b.seq, b.name])),
  );

  const save = useMutation({
    mutationFn: () =>
      reportsApi.updateZaikoKinButtons(
        buttons.map((b) => ({ seq: b.seq, name: names[b.seq] ?? '' })),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['report', 'zaiko-stock', 'kin-buttons'] });
      onClose();
    },
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">즐겨찾기 버튼 관리</h2>
        <p className="hint">상단 즐겨찾기 막대와 상품 목록의 등록 버튼에 쓸 이름을 정합니다.</p>

        <form
          className="form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          {buttons.map((b) => (
            <label key={b.seq} className="field field--full">
              <span className="field__label">즐겨찾기{b.kubun} 버튼</span>
              <input
                className="input"
                value={names[b.seq] ?? ''}
                onChange={(e) => setNames((prev) => ({ ...prev, [b.seq]: e.target.value }))}
                placeholder="버튼명을 입력하세요"
                maxLength={64}
              />
              {!CLICKABLE_KUBUN.has(b.kubun) && (
                <span className="zaiko-kin-hint">이 이름은 표시용일 뿐, 즐겨찾기 목록에는 나타나지 않습니다.</span>
              )}
            </label>
          ))}

          {save.isError && (
            <div className="qty-msg qty-msg--error">
              {save.error instanceof ApiError ? save.error.message : '저장에 실패했습니다.'}
            </div>
          )}

          <button type="submit" className="btn btn--primary btn--block" disabled={save.isPending}>
            {save.isPending ? '저장 중…' : '수정하기'}
          </button>
        </form>
      </div>
    </div>
  );
}
